import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowDownTrayIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';

import { apiClient, getErrorMessage } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import Card, { CardBody, CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import LoadingOverlay from '@/components/ui/LoadingOverlay';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import { Tabs, TabList, Tab, TabPanel } from '@/components/ui/Tabs';
import JsonExplorer from '@/components/ui/JsonExplorer';

type MistralOcrApiResponse = {
  ok: boolean;
  provider?: string;
  model?: string;
  page_index?: number;
  duration_ms?: number;
  response?: unknown;
  error?: string;
  message?: string;
  status?: number;
  statusText?: string;
  body?: unknown;
};

function fileToPreviewUrl(file: File | null): string | null {
  if (!file) return null;
  return URL.createObjectURL(file);
}

export default function AdminMistralOcrPage() {
  const navigate = useNavigate();
  const { isAdmin, isAuthenticated } = useAuth();

  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [pageIndex, setPageIndex] = useState('0');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<MistralOcrApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const previewUrl = useMemo(() => fileToPreviewUrl(file), [file]);
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Hard-gate page access in UI (backend is also protected)
  useEffect(() => {
    if (isAuthenticated && !isAdmin()) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isAdmin, navigate]);

  const jsonToRender = result?.response ?? null;

  const canSubmit = !!file && !isSubmitting;

  const onPickFile = (f: File | null) => {
    setError(null);
    setResult(null);
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!file) return;

    setIsSubmitting(true);
    setError(null);
    setResult(null);

    try {
      const fd = new FormData();
      fd.append('image', file, file.name);
      fd.append('page_index', pageIndex || '0');

      // ky instance defaults to 30s; override per-request for OCR (can take ~2 minutes)
      const data = await apiClient.postForm<MistralOcrApiResponse>('admin/ocr/mistral', fd, {
        timeout: 125_000,
      });

      setResult(data);
      if (!data?.ok) {
        setError(data?.error || 'OCR failed');
      }
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyJson = async () => {
    try {
      const text = JSON.stringify(jsonToRender, null, 2);
      await navigator.clipboard.writeText(text);
    } catch (e) {
      setError(`Copy failed: ${getErrorMessage(e)}`);
    }
  };

  const handleDownloadJson = () => {
    try {
      const text = JSON.stringify(jsonToRender, null, 2);
      const blob = new Blob([text], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mistral_ocr_result_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(`Download failed: ${getErrorMessage(e)}`);
    }
  };

  if (isAuthenticated && !isAdmin()) {
    return (
      <EmptyState
        title="Access denied"
        description="This tool is available to admins only."
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Mistral OCR</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Upload an image, run OCR, and inspect the JSON result.
        </p>
      </div>

      <LoadingOverlay
        isLoading={isSubmitting}
        loadingText="Running OCR (this can take up to ~2 minutes)…"
      >
        <Card>
          <CardHeader
            title="Upload"
            subtitle="Drag & drop an image, or click to browse."
            action={
              <Button
                variant="outline"
                size="sm"
                onClick={() => inputRef.current?.click()}
              >
                Choose file
              </Button>
            }
          />
          <CardBody className="space-y-4">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                onPickFile(f);
              }}
            />

            <div
              className={clsx(
                'rounded-2xl border-2 border-dashed p-6 transition-colors',
                dragActive
                  ? 'border-primary-400 bg-primary-50/50 dark:bg-primary-900/10'
                  : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40'
              )}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(false);
                const f = e.dataTransfer.files?.[0] || null;
                onPickFile(f);
              }}
              onClick={() => inputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
              }}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {file ? file.name : 'Drop an image here'}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {file
                      ? `${Math.round(file.size / 1024)} KB • ${file.type || 'image'}`
                      : 'PNG/JPG/WebP recommended. Max 10MB.'}
                  </div>
                </div>
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-28 h-28 object-cover rounded-xl border border-slate-200 dark:border-slate-700"
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Page index"
                value={pageIndex}
                onChange={(e) => setPageIndex(e.target.value)}
                placeholder="0"
              />
              <div className="md:col-span-2 flex items-end gap-2">
                <Button
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  disabled={!canSubmit}
                >
                  Run OCR
                </Button>
                <Button
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={() => {
                    setFile(null);
                    setResult(null);
                    setError(null);
                    setPageIndex('0');
                  }}
                >
                  Reset
                </Button>
                {isSubmitting && (
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 ml-2">
                    <Spinner size="sm" />
                    Waiting for Mistral…
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
                {error}
              </div>
            )}
          </CardBody>
        </Card>

        {result?.ok && (
          <Card>
            <CardHeader
              title="Result"
              subtitle={
                result.duration_ms != null
                  ? `Completed in ${Math.round(result.duration_ms / 1000)}s`
                  : 'OCR completed'
              }
              action={
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyJson}
                    leftIcon={<ClipboardDocumentIcon className="w-4 h-4" />}
                  >
                    Copy JSON
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadJson}
                    leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
                  >
                    Download
                  </Button>
                </div>
              }
            />
            <CardBody className="space-y-4">
              <Tabs defaultTab="explorer">
                <TabList>
                  <Tab value="explorer">Explorer</Tab>
                  <Tab value="raw">Raw</Tab>
                </TabList>

                <TabPanel value="explorer" className="mt-4">
                  <JsonExplorer value={jsonToRender} defaultExpandDepth={2} />
                </TabPanel>

                <TabPanel value="raw" className="mt-4">
                  <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                    <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 text-sm font-semibold text-slate-800 dark:text-slate-100">
                      Raw JSON
                    </div>
                    <pre className="p-4 text-sm font-mono whitespace-pre-wrap break-words text-slate-800 dark:text-slate-100 max-h-[560px] overflow-y-auto">
                      {JSON.stringify(jsonToRender, null, 2)}
                    </pre>
                  </div>
                </TabPanel>
              </Tabs>
            </CardBody>
          </Card>
        )}
      </LoadingOverlay>
    </div>
  );
}


